import { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Repeat, Trash2, Plus, CalendarClock } from "lucide-react";
import { toast } from "sonner";

/**
 * RecurrencePicker
 * ----------------
 * Compact form section for choosing an event's recurrence pattern +
 * IANA timezone. Emits `onChange({recurrence, timezone})` whenever the
 * user picks anything. `value` is `{recurrence, timezone}` — parents own
 * state.
 *
 * Patterns: One-off, Weekly, Monthly, Custom.
 *
 * We deliberately don't compute preview occurrences locally — the backend
 * is the source of truth. The parent form should surface the returned
 * `occurrences` array after saving (or after previewing via a lightweight
 * client-side expansion — see `previewOccurrences` below).
 */

const WEEKDAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const MONTHLY_NTH = [
  { key: 1, label: "First" },
  { key: 2, label: "Second" },
  { key: 3, label: "Third" },
  { key: 4, label: "Fourth" },
  { key: 5, label: "Last" },
];

// A small, curated tz list. Real orgs are ~US-centric today; UTC is
// included so servers/tools running in that zone don't fall back weirdly.
const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
];

export default function RecurrencePicker({ value, onChange, startDate }) {
  const recurrence = value?.recurrence || null;
  const timezone = value?.timezone || "America/New_York";
  const pattern = recurrence?.pattern || "oneoff";

  // Ensure the current tz is always a valid option in the Select — otherwise
  // the trigger renders blank for users whose browser tz (e.g. Asia/Kolkata)
  // isn't in the curated list.
  const tzOptions = useMemo(() => {
    if (!timezone || COMMON_TIMEZONES.includes(timezone)) return COMMON_TIMEZONES;
    return [timezone, ...COMMON_TIMEZONES];
  }, [timezone]);

  const set = (patch) => onChange({ ...(value || {}), ...patch });

  const setPattern = (nextPattern) => {
    if (nextPattern === "oneoff") {
      set({ recurrence: null });
      return;
    }
    // Seed sensible defaults when switching patterns so the picker doesn't
    // land in a half-configured state that the backend would reject.
    if (nextPattern === "weekly") {
      set({ recurrence: { pattern: "weekly", weekdays: ["sat"], until: "" } });
    } else if (nextPattern === "monthly") {
      set({ recurrence: { pattern: "monthly", monthly_nth: 1, monthly_weekday: "sat", until: "" } });
    } else if (nextPattern === "custom") {
      set({ recurrence: { pattern: "custom", custom_dates: [] } });
    }
  };

  const updateRec = (patch) => {
    set({ recurrence: { ...(recurrence || {}), ...patch } });
  };

  const toggleWeekday = (key) => {
    const days = recurrence?.weekdays || [];
    const next = days.includes(key) ? days.filter((d) => d !== key) : [...days, key];
    updateRec({ weekdays: next });
  };

  const [customInput, setCustomInput] = useState("");
  const addCustomDate = () => {
    const d = (customInput || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      toast.error("Use YYYY-MM-DD format");
      return;
    }
    const existing = recurrence?.custom_dates || [];
    if (existing.includes(d)) {
      toast.error("Already in the list");
      return;
    }
    updateRec({ custom_dates: [...existing, d].sort() });
    setCustomInput("");
  };
  const removeCustomDate = (d) => {
    updateRec({ custom_dates: (recurrence?.custom_dates || []).filter((x) => x !== d) });
  };

  // Lightweight preview so organizers see roughly which dates the series
  // will land on before saving. Backend re-computes authoritatively.
  const preview = useMemo(() => previewOccurrences(startDate, recurrence), [startDate, recurrence]);

  return (
    <div className="rounded-sm p-4 border" data-testid="recurrence-picker"
      style={{ backgroundColor: "var(--paper-warm)", borderColor: "var(--sand)" }}>
      <div className="flex items-center gap-2 mb-2">
        <Repeat className="h-4 w-4" style={{ color: "var(--moss)" }} />
        <div className="overline" style={{ color: "var(--moss)" }}>Schedule</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest">Repeats</Label>
          <Select value={pattern} onValueChange={setPattern}>
            <SelectTrigger data-testid="recurrence-pattern" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="oneoff">One-off (single date)</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="custom">Custom dates</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Timezone</Label>
          <Select value={timezone} onValueChange={(v) => set({ timezone: v })}>
            <SelectTrigger data-testid="recurrence-timezone" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {tzOptions.map((tz) => (
                <SelectItem key={tz} value={tz}>{tz.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {pattern === "weekly" && (
        <div className="mt-4" data-testid="recurrence-weekly">
          <Label className="text-xs uppercase tracking-widest">Which days?</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {WEEKDAYS.map((wd) => {
              const active = (recurrence?.weekdays || []).includes(wd.key);
              return (
                <button
                  key={wd.key}
                  type="button"
                  data-testid={`weekday-${wd.key}`}
                  onClick={() => toggleWeekday(wd.key)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
                  style={{
                    backgroundColor: active ? "var(--moss)" : "transparent",
                    color: active ? "#fbf8f2" : "var(--ink)",
                    borderColor: active ? "var(--moss)" : "var(--sand)",
                  }}
                >
                  {wd.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3">
            <Label className="text-xs uppercase tracking-widest">Stop date</Label>
            <Input
              type="date"
              data-testid="recurrence-until"
              value={recurrence?.until || ""}
              onChange={(e) => updateRec({ until: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>
      )}

      {pattern === "monthly" && (
        <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="recurrence-monthly">
          <div>
            <Label className="text-xs uppercase tracking-widest">Which</Label>
            <Select value={String(recurrence?.monthly_nth || 1)}
              onValueChange={(v) => updateRec({ monthly_nth: parseInt(v, 10) })}>
              <SelectTrigger data-testid="monthly-nth" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHLY_NTH.map((n) => <SelectItem key={n.key} value={String(n.key)}>{n.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Weekday</Label>
            <Select value={recurrence?.monthly_weekday || "sat"}
              onValueChange={(v) => updateRec({ monthly_weekday: v })}>
              <SelectTrigger data-testid="monthly-weekday" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WEEKDAYS.map((wd) => <SelectItem key={wd.key} value={wd.key}>{wd.label}day</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Stop date</Label>
            <Input
              type="date"
              data-testid="recurrence-until"
              value={recurrence?.until || ""}
              onChange={(e) => updateRec({ until: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>
      )}

      {pattern === "custom" && (
        <div className="mt-4" data-testid="recurrence-custom">
          <Label className="text-xs uppercase tracking-widest">Add a date (YYYY-MM-DD)</Label>
          <div className="mt-1 flex gap-2">
            <Input
              type="date"
              data-testid="custom-date-input"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              className="max-w-[220px]"
            />
            <Button type="button" onClick={addCustomDate}
              data-testid="custom-date-add" className="btn-outline rounded-full">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add
            </Button>
          </div>
          {(recurrence?.custom_dates || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="custom-date-list">
              {(recurrence?.custom_dates || []).map((d) => (
                <span key={d}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: "var(--moss)", color: "#fbf8f2" }}>
                  {d}
                  <button type="button" onClick={() => removeCustomDate(d)}
                    data-testid={`custom-date-remove-${d}`}
                    className="ml-1 opacity-80 hover:opacity-100">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {pattern !== "oneoff" && preview.length > 0 && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--sand)" }}
          data-testid="recurrence-preview">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="h-3.5 w-3.5" style={{ color: "var(--ink-soft)" }} />
            <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
              Preview · {preview.length} date{preview.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {preview.slice(0, 20).map((d) => (
              <span key={d} className="text-[11px] px-2 py-0.5 rounded"
                style={{ backgroundColor: "var(--paper-warm)", border: "1px solid var(--sand)",
                         color: "var(--ink-soft)" }}>
                {formatShort(d)}
              </span>
            ))}
            {preview.length > 20 && (
              <span className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
                +{preview.length - 20} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatShort(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch {
    return iso;
  }
}

// Client-side mirror of backend expansion — kept intentionally simple so we
// can render a live preview. Backend re-computes on save.
function previewOccurrences(startDate, rec) {
  if (!startDate || !rec) return [];
  const start = parseISO(startDate);
  if (!start) return [];
  const cap = 52;
  const dates = new Set([iso(start)]);

  if (rec.pattern === "weekly") {
    const wd = (rec.weekdays || []).map((k) => ["mon","tue","wed","thu","fri","sat","sun"].indexOf(k));
    if (wd.length === 0) return [iso(start)];
    const until = parseISO(rec.until) || addDays(start, 90);
    let d = new Date(start);
    while (d <= until && dates.size < cap) {
      const wd0 = (d.getDay() + 6) % 7; // convert JS Sun=0 to Mon=0
      if (wd.includes(wd0)) dates.add(iso(d));
      d = addDays(d, 1);
    }
  } else if (rec.pattern === "monthly") {
    const wd = ["mon","tue","wed","thu","fri","sat","sun"].indexOf(rec.monthly_weekday);
    const nth = rec.monthly_nth;
    if (wd < 0 || !nth) return [iso(start)];
    const until = parseISO(rec.until) || addDays(start, 365);
    let year = start.getFullYear();
    let month = start.getMonth();
    while (true) {
      const cand = nthWeekdayOfMonth(year, month, wd, nth);
      if (cand && cand >= start && cand <= until) dates.add(iso(cand));
      month++;
      if (month > 11) { month = 0; year++; }
      const nextFirst = new Date(year, month, 1);
      if (nextFirst > until || dates.size >= cap) break;
    }
  } else if (rec.pattern === "custom") {
    for (const s of (rec.custom_dates || [])) {
      const d = parseISO(s);
      if (d && d >= start) dates.add(iso(d));
    }
  }
  return [...dates].sort();
}

function parseISO(s) {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? null : d;
}
function iso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function nthWeekdayOfMonth(year, month, weekdayMonZero, nth) {
  // Convert Mon-zero index back to JS Sun-zero for Date arithmetic.
  const jsWeekday = (weekdayMonZero + 1) % 7;
  if (nth === 5) {
    // last occurrence — walk back from last day of month
    const last = new Date(year, month + 1, 0);
    let d = last;
    while (d.getDay() !== jsWeekday) d = addDays(d, -1);
    return d;
  }
  if (nth < 1 || nth > 4) return null;
  let d = new Date(year, month, 1);
  const offset = (jsWeekday - d.getDay() + 7) % 7;
  d = addDays(d, offset + 7 * (nth - 1));
  if (d.getMonth() !== month) return null;
  return d;
}
